import { get, set, has } from 'lodash-es';
import { formValue } from 'site-modules/shared/utils/string';

export const IDM_PATH_MAP = new Map([
  ['firstName', { val: 'person.firstName', def: '' }],
  ['lastName', { val: 'pii.lastName', def: '' }],
  ['gender', { val: 'pii.gender', def: '' }],
  ['phoneNumbers', { val: 'pii.phoneNumbers', def: '' }],
  ['address', { val: 'pii.address', def: '' }],
  ['zipCode', { val: 'pii.address.postalCode', def: '' }],
  ['lastGeoLocation', { val: 'person.location.lastGeoLocation.postalCode', def: '' }],
  ['researchPostalCodes', { val: 'person.researchPostalCodes', def: '' }],
  ['emails', { val: 'identifiers.email', def: '' }],
  ['preventThirdPartySharing', { val: 'meta.preventThirdPartySharing', def: false }],
]);

export const POSTAL_CODE_MAP = new Map([['zipCode', { val: 'address.postalCode', def: '' }]]);

export const PHONE_PATH_MAP = new Map([
  ['phoneType', { val: 'phoneNumbers.0.phoneType', def: '' }],
  ['areaCode', { val: 'phoneNumbers.0.areaCode', def: '' }],
  ['number', { val: 'phoneNumbers.0.number', def: '' }],
]);

/**
 * Gets and flattens IDM values according to provided map
 * @param pathMap With pathways to values
 * @param idmProfile IDM data
 * @returns {Object}  With required IDM values
 */
export const getIdmValues = (pathMap, idmProfile) => {
  const values = {};
  pathMap.forEach(({ val, def }, key) => {
    set(values, key, get(idmProfile, val, def));
  });
  return values;
};

/**
 * Gets and flattens values for form
 * @param profileData  IDM data
 * @returns {Object}  With required IDM values
 */
let idmValues;

export const getFormValues = profileData => {
  idmValues = getIdmValues(IDM_PATH_MAP, profileData);
  const addressData = getIdmValues(POSTAL_CODE_MAP, idmValues);
  const phoneData = getIdmValues(PHONE_PATH_MAP, idmValues);

  return {
    firstName: idmValues.firstName,
    lastName: idmValues.lastName,
    gender: idmValues.gender,
    phone: `${phoneData.areaCode}${phoneData.number}`,
    phoneType: phoneData.phoneType,
    email: idmValues.emails[0],
    ...addressData,
  };
};

/**
 * If phone field has changed, splits phone into number and areaCode
 * @param filteredFormValues  Values from form that have changed
 * @param formValues  All values from form
 * @returns {Array}  phoneNumbers with changed fields (divided) or empty array
 */
export const setPhoneNumbers = (filteredFormValues, formValues) => {
  if (formValues.phone) {
    set(filteredFormValues, PHONE_PATH_MAP.get('areaCode').val, formValues.phone.substr(0, 3));
    set(filteredFormValues, PHONE_PATH_MAP.get('number').val, formValues.phone.substr(3));
    if (formValues.phoneType) {
      set(filteredFormValues, PHONE_PATH_MAP.get('phoneType').val, formValues.phoneType);
    }
  } else {
    set(filteredFormValues, 'phoneNumbers', '');
  }
  return filteredFormValues;
};

/**
 * If the zipCode has changed, should add its values to result
 * @param filteredFormValues  Values from form that have changed
 * @param formValues  zipCode value from form
 * @returns {Object}  Filtered values with not empty address fields
 */
export const setZipCode = (filteredFormValues, formValues) => {
  const ZIPCODE_MAP = ['zipCode'];

  return ZIPCODE_MAP.reduce((result, value) => {
    if (formValues[value]) {
      set(filteredFormValues, POSTAL_CODE_MAP.get(value).val, formValues[value]);
    }
    return result;
  }, filteredFormValues);
};

/**
 * Filters only updated fields, splits up phone value
 * @param initialFormValues  Initial form values
 * @param formValues  Values from form after submit
 * @returns {Object}  With changed fields
 */
export const filterFormValues = (initialFormValues, formValues) => {
  const changedValues = {};
  if (formValues.phone) {
    set(formValues, 'phone', formValues.phone.replace(/\D/g, ''));
  }
  Object.keys(initialFormValues).forEach(key => {
    const currentValue = formValue(formValues[key]);
    if (initialFormValues[key] !== currentValue) {
      changedValues[key] = currentValue;
    }
  });

  /* checks key existence, even if value is "" */
  if (has(changedValues, 'phone') || (has(changedValues, 'phoneType') && formValues.phone)) {
    setPhoneNumbers(changedValues, formValues);
  }
  if (has(changedValues, 'zipCode')) {
    setZipCode(changedValues, formValues);
  }

  return changedValues;
};

/**
 * Checks that the field was updated, even if the value is empty
 * @param value - field's value
 * @returns {boolean} isFieldUpdated
 */
export const isFieldUpdated = value => value !== undefined;

/**
 * Formats to IDM data structure
 * @param data Updated values
 * @returns {Object} With updated IDM values
 */
export const formatToIdm = data => {
  const idmUpdatedValues = {};

  IDM_PATH_MAP.forEach(({ val }, key) => {
    if (isFieldUpdated(data[key])) {
      if (data[key]) {
        set(idmUpdatedValues, `updateData.${val}`, data[key]);
      } else {
        set(idmUpdatedValues, `deleteData.${val}`, idmValues[key]);
      }
    }
  });

  return idmUpdatedValues;
};
